import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FileCode2, Download, Banknote, Plus, Trash2, Loader2, Settings2, FileSpreadsheet, Send, X } from 'lucide-react';
import { useData } from '../context/DataContext';
import { carsysApi } from '../api/carsysApi';
import { confirmDialog } from '../lib/confirm';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
const fmtDate = (s) => { if (!s) return '—'; const d = new Date(s); return isNaN(d) ? s : d.toLocaleDateString('nl-NL'); };

const blobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
};

export default function Efacturatie() {
  const { t } = useT();
  const { klanten, bonnen } = useData();
  const [tab, setTab] = useState('ubl');

  const facturen = useMemo(() => (bonnen || []).filter(b => b.type === 'Factuur'), [bonnen]);
  const klantNaam = (id) => (klanten.find(k => k.id === id) || {}).naam || '—';

  // ---- UBL download ----
  const [ublBusy, setUblBusy] = useState('');
  // ---- Peppol (Storecove) ----
  const [peppol, setPeppol] = useState({ apiKeySet: false, legalEntityId: '', apiKey: '' });
  const [peppolSaving, setPeppolSaving] = useState(false);
  const [ppModal, setPpModal] = useState(null); // {bid, nummer}
  const [ppRecipient, setPpRecipient] = useState({ scheme: '0106', id: '' });
  const [ppBusy, setPpBusy] = useState(false);
  useEffect(() => { if (tab === 'ubl') carsysApi.getPeppolInstellingen().then(p => setPeppol(s => ({ ...s, ...p }))).catch(() => {}); }, [tab]);

  const savePeppol = async () => {
    setPeppolSaving(true);
    try { const p = await carsysApi.setPeppolInstellingen({ legalEntityId: peppol.legalEntityId, apiKey: peppol.apiKey }); setPeppol(s => ({ ...s, ...p, apiKey: '' })); toast.success(t('Opgeslagen', 'Opgeslagen')); }
    catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setPeppolSaving(false); }
  };
  const sendPeppol = async () => {
    setPpBusy(true);
    try {
      const r = await carsysApi.sendPeppol(ppModal.bid, { recipientScheme: ppRecipient.scheme, recipientId: ppRecipient.id });
      toast.success(r.mock ? t('Peppol (concept)', 'Peppol (concept)') : t('Verzonden via Peppol', 'Verzonden via Peppol'), { description: r.status });
      setPpModal(null);
    } catch (e) { toast.error(t('Versturen mislukt', 'Versturen mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setPpBusy(false); }
  };
  const downloadUbl = async (b) => {
    setUblBusy(b.id);
    try {
      const blob = await carsysApi.downloadUbl(b.id);
      blobDownload(blob, `efactuur-${(b.nummer || b.id).replace(/\//g, '-')}.xml`);
      toast.success(t('UBL-bestand gedownload', 'UBL-bestand gedownload'));
    } catch (e) {
      toast.error(t('Download mislukt', 'Download mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setUblBusy(''); }
  };

  // ---- SEPA settings ----
  const [sepa, setSepa] = useState({ incassantId: '', incassantNaam: '', incassantIban: '' });
  const [sepaSaving, setSepaSaving] = useState(false);
  const [machtigingen, setMachtigingen] = useState([]);
  const [incasseerbaar, setIncasseerbaar] = useState([]);
  const [sel, setSel] = useState({});
  const [collDate, setCollDate] = useState(new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10));
  const [batchBusy, setBatchBusy] = useState(false);
  const [mForm, setMForm] = useState({ klantId: '', iban: '', mandaatKenmerk: '', ondertekendDatum: new Date().toISOString().slice(0, 10), type: 'RCUR' });
  const [mSaving, setMSaving] = useState(false);

  const loadSepa = useCallback(() => {
    carsysApi.getSepaInstellingen().then(setSepa).catch(() => {});
    carsysApi.getSepaMachtigingen().then(setMachtigingen).catch(() => setMachtigingen([]));
    carsysApi.getSepaIncasseerbaar().then(setIncasseerbaar).catch(() => setIncasseerbaar([]));
  }, []);
  useEffect(() => { if (tab === 'sepa') loadSepa(); }, [tab, loadSepa]);

  const saveSepa = async () => {
    setSepaSaving(true);
    try { setSepa(await carsysApi.setSepaInstellingen(sepa)); toast.success(t('Opgeslagen', 'Opgeslagen')); }
    catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSepaSaving(false); }
  };

  const addMachtiging = async () => {
    if (!mForm.klantId) { toast.error(t('Kies een klant', 'Kies een klant')); return; }
    if (!mForm.iban || mForm.iban.replace(/\s/g, '').length < 15) { toast.error(t('Geef een geldig IBAN op', 'Geef een geldig IBAN op')); return; }
    setMSaving(true);
    try {
      await carsysApi.createSepaMachtiging(mForm);
      toast.success(t('Machtiging toegevoegd', 'Machtiging toegevoegd'));
      setMForm({ klantId: '', iban: '', mandaatKenmerk: '', ondertekendDatum: new Date().toISOString().slice(0, 10), type: 'RCUR' });
      loadSepa();
    } catch (e) { toast.error(t('Toevoegen mislukt', 'Toevoegen mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setMSaving(false); }
  };

  const delMachtiging = async (m) => {
    if (!(await confirmDialog(t('Machtiging verwijderen?', 'Machtiging verwijderen?')))) return;
    try { await carsysApi.deleteSepaMachtiging(m.id); toast.success(t('Verwijderd', 'Verwijderd')); loadSepa(); }
    catch (e) { toast.error(t('Verwijderen mislukt', 'Verwijderen mislukt')); }
  };

  const selectedIds = Object.keys(sel).filter(k => sel[k]);
  const selectedTotal = incasseerbaar.filter(i => sel[i.id]).reduce((s, i) => s + i.openstaand, 0);

  const genBatch = async () => {
    if (!selectedIds.length) { toast.error(t('Selecteer minimaal één factuur', 'Selecteer minimaal één factuur')); return; }
    setBatchBusy(true);
    try {
      const blob = await carsysApi.genereerSepaBatch({ factuurIds: selectedIds, collectionDate: collDate });
      blobDownload(blob, `sepa-incasso-${collDate}.xml`);
      toast.success(t('Incassobatch gegenereerd', 'Incassobatch gegenereerd'));
      setSel({});
    } catch (e) {
      let msg = e.response?.data?.detail || e.message;
      if (e.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail; } catch (_) { /* noop */ } }
      toast.error(t('Genereren mislukt', 'Genereren mislukt'), { description: msg });
    } finally { setBatchBusy(false); }
  };

  return (
    <div className="cs-page" data-testid="efacturatie-page" style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <FileCode2 size={22} color="#2563eb" />
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#0f172a' }}>{t('E-facturatie & SEPA-incasso', 'E-facturatie & SEPA-incasso')}</h1>
      </div>
      <p style={{ color: '#64748b', margin: '0 0 18px', fontSize: 14 }}>
        {t('Genereer geldige UBL/Peppol-e-facturen en SEPA-incassobatches voor je bank.', 'Genereer geldige UBL/Peppol-e-facturen en SEPA-incassobatches voor je bank.')}
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        <button data-testid="ef-tab-ubl" onClick={() => setTab('ubl')} style={tabBtn(tab === 'ubl')}><FileCode2 size={15} /> {t('E-facturen (UBL)', 'E-facturen (UBL)')}</button>
        <button data-testid="ef-tab-sepa" onClick={() => setTab('sepa')} style={tabBtn(tab === 'sepa')}><Banknote size={15} /> {t('SEPA-incasso', 'SEPA-incasso')}</button>
      </div>

      {tab === 'ubl' && (
        <div style={card} data-testid="ef-ubl-panel">
          <div style={{ fontSize: 13.5, color: '#64748b', marginBottom: 12 }}>
            {t('Download een Peppol BIS 3.0 (NLCIUS) UBL-bestand per factuur. Upload dit bij je eigen Peppol Access Point of boekhoudpakket.', 'Download een Peppol BIS 3.0 (NLCIUS) UBL-bestand per factuur. Upload dit bij je eigen Peppol Access Point of boekhoudpakket.')}
          </div>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 14 }} data-testid="ef-peppol-settings">
            <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a', marginBottom: 8 }}>{t('Peppol-verzending (Storecove)', 'Peppol-verzending (Storecove)')}</div>
            <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10 }}>
              {t('Optioneel: koppel je Storecove Access Point om e-facturen écht over Peppol te versturen. Zonder koppeling kun je de UBL downloaden.', 'Optioneel: koppel je Storecove Access Point om e-facturen écht over Peppol te versturen. Zonder koppeling kun je de UBL downloaden.')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              <Field label={`Storecove API-key ${peppol.apiKeySet ? '(' + t('ingesteld', 'ingesteld') + ')' : ''}`}>
                <input type="password" style={inp} data-testid="ef-peppol-key" value={peppol.apiKey || ''} onChange={e => setPeppol({ ...peppol, apiKey: e.target.value })} placeholder={peppol.apiKeySet ? '********' : ''} />
              </Field>
              <Field label="Legal Entity ID">
                <input style={inp} data-testid="ef-peppol-legalentity" value={peppol.legalEntityId || ''} onChange={e => setPeppol({ ...peppol, legalEntityId: e.target.value })} placeholder="12345" />
              </Field>
            </div>
            <button style={{ ...smallBtn, marginTop: 10 }} data-testid="ef-peppol-save" onClick={savePeppol} disabled={peppolSaving}>
              {peppolSaving ? <Loader2 size={13} className="spin" /> : null} {t('Peppol-instellingen opslaan', 'Peppol-instellingen opslaan')}
            </button>
          </div>
          {facturen.length === 0 ? <Empty>{t('Nog geen facturen', 'Nog geen facturen')}</Empty> : (
            <table style={tbl}>
              <thead><tr>
                <Th>{t('Nummer', 'Nummer')}</Th><Th>{t('Datum', 'Datum')}</Th><Th>{t('Klant', 'Klant')}</Th>
                <Th right>{t('Totaal', 'Totaal')}</Th><Th right>{t('Actie', 'Actie')}</Th>
              </tr></thead>
              <tbody>
                {facturen.map(b => (
                  <tr key={b.id} data-testid={`ef-factuur-${b.id}`} style={{ borderTop: '1px solid #eef2f7' }}>
                    <Td><b>{b.nummer}</b></Td>
                    <Td>{fmtDate(b.datum)}</Td>
                    <Td>{klantNaam(b.klantId)}</Td>
                    <Td right>{eur(b.totaal)}</Td>
                    <Td right>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button style={smallBtn} data-testid={`ef-ubl-download-${b.id}`} disabled={ublBusy === b.id} onClick={() => downloadUbl(b)}>
                          {ublBusy === b.id ? <Loader2 size={13} className="spin" /> : <Download size={13} />} UBL
                        </button>
                        <button style={ghostBtn} data-testid={`ef-peppol-send-${b.id}`} onClick={() => { setPpRecipient({ scheme: '0106', id: '' }); setPpModal({ bid: b.id, nummer: b.nummer }); }}>
                          <Send size={13} /> Peppol
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'sepa' && (
        <div style={{ display: 'grid', gap: 16 }}>
          {/* Instellingen */}
          <div style={card} data-testid="ef-sepa-settings">
            <div style={sectTitle}><Settings2 size={16} /> {t('Incassant-instellingen', 'Incassant-instellingen')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <Field label={t('Incassant ID (creditor identifier)', 'Incassant ID (creditor identifier)')}>
                <input style={inp} data-testid="ef-incassant-id" value={sepa.incassantId || ''} onChange={e => setSepa({ ...sepa, incassantId: e.target.value })} placeholder="NL98ZZZ123456780000" />
              </Field>
              <Field label={t('Naam incassant', 'Naam incassant')}>
                <input style={inp} data-testid="ef-incassant-naam" value={sepa.incassantNaam || ''} onChange={e => setSepa({ ...sepa, incassantNaam: e.target.value })} />
              </Field>
              <Field label={t('IBAN incassant', 'IBAN incassant')}>
                <input style={inp} data-testid="ef-incassant-iban" value={sepa.incassantIban || ''} onChange={e => setSepa({ ...sepa, incassantIban: e.target.value })} placeholder="NL00BANK0123456789" />
              </Field>
            </div>
            <button style={{ ...primaryBtn, marginTop: 12, width: 'auto' }} data-testid="ef-sepa-save" onClick={saveSepa} disabled={sepaSaving}>
              {sepaSaving ? <Loader2 size={15} className="spin" /> : null} {t('Opslaan', 'Opslaan')}
            </button>
          </div>

          {/* Machtigingen */}
          <div style={card} data-testid="ef-machtigingen">
            <div style={sectTitle}><Banknote size={16} /> {t('Machtigingen', 'Machtigingen')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.3fr 1fr 1fr auto', gap: 8, alignItems: 'end', marginBottom: 14 }}>
              <Field label={t('Klant', 'Klant')}>
                <select style={inp} data-testid="ef-machtiging-klant" value={mForm.klantId} onChange={e => setMForm({ ...mForm, klantId: e.target.value })}>
                  <option value="">{t('Kies klant…', 'Kies klant…')}</option>
                  {klanten.map(k => <option key={k.id} value={k.id}>{k.naam}</option>)}
                </select>
              </Field>
              <Field label="IBAN"><input style={inp} data-testid="ef-machtiging-iban" value={mForm.iban} onChange={e => setMForm({ ...mForm, iban: e.target.value.toUpperCase() })} placeholder="NL00BANK0123456789" /></Field>
              <Field label={t('Mandaatkenmerk', 'Mandaatkenmerk')}><input style={inp} data-testid="ef-machtiging-kenmerk" value={mForm.mandaatKenmerk} onChange={e => setMForm({ ...mForm, mandaatKenmerk: e.target.value })} placeholder={t('auto', 'auto')} /></Field>
              <Field label={t('Ondertekend', 'Ondertekend')}><input type="date" style={inp} data-testid="ef-machtiging-datum" value={mForm.ondertekendDatum} onChange={e => setMForm({ ...mForm, ondertekendDatum: e.target.value })} /></Field>
              <button style={primaryBtn} data-testid="ef-machtiging-add" onClick={addMachtiging} disabled={mSaving}>
                {mSaving ? <Loader2 size={15} className="spin" /> : <Plus size={15} />} {t('Toevoegen', 'Toevoegen')}
              </button>
            </div>
            {machtigingen.length === 0 ? <Empty>{t('Nog geen machtigingen', 'Nog geen machtigingen')}</Empty> : (
              <table style={tbl}>
                <thead><tr><Th>{t('Klant', 'Klant')}</Th><Th>IBAN</Th><Th>{t('Kenmerk', 'Kenmerk')}</Th><Th>{t('Ondertekend', 'Ondertekend')}</Th><Th>{t('Type', 'Type')}</Th><Th right></Th></tr></thead>
                <tbody>
                  {machtigingen.map(m => (
                    <tr key={m.id} data-testid={`ef-machtiging-${m.id}`} style={{ borderTop: '1px solid #eef2f7' }}>
                      <Td>{m.klantNaam}</Td><Td style={{ fontFamily: 'ui-monospace, monospace' }}>{m.iban}</Td>
                      <Td>{m.mandaatKenmerk}</Td><Td>{fmtDate(m.ondertekendDatum)}</Td><Td>{m.type}</Td>
                      <Td right><button style={iconDanger} data-testid={`ef-machtiging-del-${m.id}`} onClick={() => delMachtiging(m)}><Trash2 size={14} /></button></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Incassobatch */}
          <div style={card} data-testid="ef-batch">
            <div style={sectTitle}><FileSpreadsheet size={16} /> {t('Incassobatch genereren (pain.008)', 'Incassobatch genereren (pain.008)')}</div>
            {incasseerbaar.length === 0 ? <Empty>{t('Geen openstaande facturen met een actieve machtiging', 'Geen openstaande facturen met een actieve machtiging')}</Empty> : (
              <>
                <table style={tbl}>
                  <thead><tr>
                    <Th></Th><Th>{t('Nummer', 'Nummer')}</Th><Th>{t('Klant', 'Klant')}</Th><Th>{t('Datum', 'Datum')}</Th><Th right>{t('Openstaand', 'Openstaand')}</Th>
                  </tr></thead>
                  <tbody>
                    {incasseerbaar.map(i => (
                      <tr key={i.id} data-testid={`ef-incasso-${i.id}`} style={{ borderTop: '1px solid #eef2f7' }}>
                        <Td><input type="checkbox" data-testid={`ef-incasso-check-${i.id}`} checked={!!sel[i.id]} onChange={e => setSel({ ...sel, [i.id]: e.target.checked })} /></Td>
                        <Td><b>{i.nummer}</b></Td><Td>{i.klantNaam}</Td><Td>{fmtDate(i.datum)}</Td><Td right>{eur(i.openstaand)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', alignItems: 'end', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
                  <Field label={t('Incassodatum', 'Incassodatum')}>
                    <input type="date" style={inp} data-testid="ef-batch-date" value={collDate} onChange={e => setCollDate(e.target.value)} />
                  </Field>
                  <div style={{ fontSize: 13.5, color: '#475569' }} data-testid="ef-batch-summary">
                    {selectedIds.length} {t('geselecteerd', 'geselecteerd')} · <b>{eur(selectedTotal)}</b>
                  </div>
                  <button style={{ ...primaryBtn, width: 'auto' }} data-testid="ef-batch-generate" onClick={genBatch} disabled={batchBusy || !selectedIds.length}>
                    {batchBusy ? <Loader2 size={15} className="spin" /> : <Download size={15} />} {t('Genereer SEPA-batch', 'Genereer SEPA-batch')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      {ppModal && (
        <div style={overlay} data-testid="ef-peppol-modal" onClick={() => !ppBusy && setPpModal(null)}>
          <div style={{ ...card, maxWidth: 440, width: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>{t('Verstuur via Peppol', 'Verstuur via Peppol')} · {ppModal.nummer}</div>
              <button style={iconBtnSm} data-testid="ef-peppol-close" onClick={() => setPpModal(null)}><X size={16} /></button>
            </div>
            <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 12px' }}>
              {t('Geef de Peppol-identificatie van de ontvanger op (bijv. KvK-scheme 0106).', 'Geef de Peppol-identificatie van de ontvanger op (bijv. KvK-scheme 0106).')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
              <Field label={t('Scheme', 'Scheme')}><input style={inp} data-testid="ef-peppol-rscheme" value={ppRecipient.scheme} onChange={e => setPpRecipient({ ...ppRecipient, scheme: e.target.value })} placeholder="0106" /></Field>
              <Field label={t('Ontvanger-ID', 'Ontvanger-ID')}><input style={inp} data-testid="ef-peppol-rid" value={ppRecipient.id} onChange={e => setPpRecipient({ ...ppRecipient, id: e.target.value })} placeholder="12345678" /></Field>
            </div>
            <button style={{ ...primaryBtn, width: '100%', marginTop: 14 }} data-testid="ef-peppol-confirm" onClick={sendPeppol} disabled={ppBusy}>
              {ppBusy ? <Loader2 size={15} className="spin" /> : <Send size={15} />} {t('Versturen', 'Versturen')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => <div><label style={{ fontSize: 12.5, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 5 }}>{label}</label>{children}</div>;
const Th = ({ children, right }) => <th style={{ textAlign: right ? 'right' : 'left', padding: '8px 10px', fontSize: 12, color: '#64748b', fontWeight: 700 }}>{children}</th>;
const Td = ({ children, right, style }) => <td style={{ textAlign: right ? 'right' : 'left', padding: '9px 10px', fontSize: 13.5, color: '#0f172a', ...style }}>{children}</td>;
const Empty = ({ children }) => <div style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0', fontSize: 14 }}>{children}</div>;

const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, boxShadow: '0 2px 6px rgba(15,23,42,.04)' };
const tbl = { width: '100%', borderCollapse: 'collapse' };
const sectTitle = { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 14 };
const inp = { width: '100%', padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13.5, boxSizing: 'border-box' };
const primaryBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 16px', border: 'none', borderRadius: 8, background: '#2563eb', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' };
const smallBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: '1px solid #2563eb', borderRadius: 7, background: '#eff6ff', color: '#2563eb', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' };
const ghostBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 7, background: '#fff', color: '#334155', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 };
const iconBtnSm = { background: '#f1f5f9', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#475569' };
const iconDanger = { display: 'inline-flex', padding: 6, border: 'none', borderRadius: 7, background: '#fef2f2', color: '#dc2626', cursor: 'pointer' };
const tabBtn = (active) => ({ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', border: '1px solid', borderColor: active ? '#2563eb' : '#e2e8f0', background: active ? '#2563eb' : '#fff', color: active ? '#fff' : '#475569', borderRadius: 9, fontWeight: 700, cursor: 'pointer', fontSize: 13.5 });
